/**
 * Supplier–product relationship domain service.
 *
 * The single canonical write path for `supplier_products` links, used whether
 * the link is initiated from the Product editor or the Supplier editor. Both
 * entry points share this service so the relationship has exactly one write
 * implementation (no duplicated persistence logic across pages).
 *
 * These are RLS-guarded single-table writes with no cross-table invariant at
 * write time; the DB constraint and org-scoped RLS enforce uniqueness/tenancy.
 * Errors are normalised through `friendlyError` so the UI shows a domain
 * message rather than a raw constraint string.
 */

import { supabase } from "@/lib/supabase";
import { friendlyError } from "@/lib/errors";
import { getActiveCurrencyCode } from "@/lib/currency";

export interface SupplierProductLinkInput {
  productId: string;
  supplierId: string;
  variantId: string | null;
  supplierSku: string | null;
  supplierPrice: number | null;
  minOrderQty: number | null;
  orderMultiple: number | null;
  leadTimeDays: number | null;
  isPreferred: boolean;
  /** Currency for `supplierPrice`. Defaults to the organisation's active currency. */
  currency?: string | null;
}

/**
 * Link a supplier to a product (optionally scoped to a specific variant).
 * Throws a friendly `Error` on failure.
 */
export async function linkSupplierProduct(
  orgId: string,
  input: SupplierProductLinkInput,
): Promise<void> {
  // Resolve the relationship currency: an explicit value wins, otherwise the
  // organisation's active currency (never a silent USD default for an AUD org).
  const currency = input.currency || getActiveCurrencyCode();

  // Idempotent natural key: one link per (product, supplier, variant) per org.
  // This makes a retry safe — it updates the existing row instead of inserting a
  // duplicate — and mirrors the duplicate-guard already used by the CSV importer.
  let lookup = supabase
    .from("supplier_products")
    .select("id")
    .eq("org_id", orgId)
    .eq("product_id", input.productId)
    .eq("supplier_id", input.supplierId);
  lookup = input.variantId
    ? lookup.eq("variant_id", input.variantId)
    : lookup.is("variant_id", null);

  const { data: existing, error: lookErr } = await lookup.limit(1);
  if (lookErr) throw new Error(friendlyError(lookErr.message));

  const payload = {
    supplier_sku: input.supplierSku || null,
    supplier_price: input.supplierPrice,
    min_order_qty: input.minOrderQty ?? 1,
    order_multiple: input.orderMultiple,
    lead_time_days: input.leadTimeDays,
    is_preferred: input.isPreferred,
    currency,
  };

  if (existing && existing.length > 0) {
    const { error } = await supabase
      .from("supplier_products")
      .update(payload)
      .eq("id", existing[0].id);
    if (error) throw new Error(friendlyError(error.message));
    return;
  }

  const { error } = await supabase.from("supplier_products").insert({
    org_id: orgId,
    product_id: input.productId,
    supplier_id: input.supplierId,
    variant_id: input.variantId || null,
    ...payload,
  });
  if (error) throw new Error(friendlyError(error.message));
}

/**
 * Remove a supplier–product link, scoped to the caller's organisation.
 * Throws a friendly `Error` on failure.
 */
export async function unlinkSupplierProduct(orgId: string, linkId: string): Promise<void> {
  const { error } = await supabase
    .from("supplier_products")
    .delete()
    .eq("id", linkId)
    .eq("org_id", orgId);
  if (error) throw new Error(friendlyError(error.message));
}